import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Star, UploadSimple, Sparkle, ArrowRight, Crown } from '@phosphor-icons/react';

import { bubbleImageSrc } from '@/lib/bubbleImage';
const RARITIES = ['rare', 'epic', 'legendary', 'mythic'];

const Curator = () => {
  const [drops, setDrops] = useState([]);
  const [payouts, setPayouts] = useState({ by_celebrity: [], payouts: [] });
  const [causes, setCauses] = useState([]);
  const [form, setForm] = useState({
    celebrity_name: '',
    drop_name: '',
    tagline: '',
    face_ref_b64: '',
    price_usd: 99,
    supply: 25,
    rarity: 'legendary',
    cause_id: '',
    split_pct_to_cause: 30,
    split_pct_to_celebrity: 50,
  });
  const [filename, setFilename] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [d, p, c] = await Promise.all([
      api.get('/admin/celebrity-drops'),
      api.get('/admin/celebrity-payouts'),
      api.get('/charities'),
    ]);
    setDrops(d.data.drops);
    setPayouts(p.data);
    setCauses(c.data.charities);
  };
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('image too large (max 5MB)'); return; }
    setFilename(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const b64 = result.split(',')[1] || '';
      setForm((f) => ({ ...f, face_ref_b64: b64 }));
    };
    reader.readAsDataURL(file);
  };

  const set = (k, parser = (v) => v) => (e) => setForm({ ...form, [k]: parser(e.target.value) });
  const platformPct = 100 - form.split_pct_to_cause - form.split_pct_to_celebrity;

  const submit = async () => {
    if (!form.face_ref_b64) { toast.error('upload a face reference image'); return; }
    if (platformPct < 0) { toast.error('splits exceed 100%'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/admin/celebrity-drops', form);
      toast.success(`Drop "${data.drop.name}" created`);
      setForm({
        celebrity_name: '', drop_name: '', tagline: '', face_ref_b64: '',
        price_usd: 99, supply: 25, rarity: 'legendary', cause_id: '',
        split_pct_to_cause: 30, split_pct_to_celebrity: 50,
      });
      setFilename('');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 pb-24">
      <div className="flex items-center gap-2 mb-6">
        <Star size={28} weight="duotone" className="text-bx-gold" />
        <h1 className="font-display text-4xl font-black tracking-tighter">Celebrity Curator</h1>
      </div>
      <p className="text-white/60 max-w-2xl mb-8">
        Upload a face reference, set the supply cap and revenue split. Each buyer gets a one-of-one AI-generated celebrity-style bubble at mint time. The face reference stays private; only the stylized bubble ships to the user.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Form */}
        <div className="lg:col-span-7 glass rounded-2xl p-6 space-y-4">
          <h2 className="font-display text-xl font-bold mb-2">Create a private drop</h2>

          <label className="block">
            <span className="text-xs uppercase tracking-widest text-white/65">Celebrity name</span>
            <input data-testid="curator-celeb" value={form.celebrity_name} onChange={set('celebrity_name')} placeholder="e.g. Aurora Vega" className="mt-1 w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm" />
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-widest text-white/65">Drop name</span>
            <input data-testid="curator-drop-name" value={form.drop_name} onChange={set('drop_name')} placeholder='e.g. "Aurora — Signed Series"' className="mt-1 w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm" />
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-widest text-white/65">Tagline</span>
            <input data-testid="curator-tagline" value={form.tagline} onChange={set('tagline')} placeholder="Short, evocative." className="mt-1 w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm" />
          </label>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-white/65">Price (USD)</span>
              <input data-testid="curator-price" type="number" min="1" step="0.01" value={form.price_usd} onChange={set('price_usd', parseFloat)} className="mt-1 w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-white/65">Supply</span>
              <input data-testid="curator-supply" type="number" min="1" max="10000" value={form.supply} onChange={set('supply', parseInt)} className="mt-1 w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-white/65">Rarity</span>
              <select data-testid="curator-rarity" value={form.rarity} onChange={set('rarity')} className="mt-1 w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm">
                {RARITIES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-white/65">Cause (optional)</span>
              <select data-testid="curator-cause" value={form.cause_id} onChange={set('cause_id')} className="mt-1 w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm">
                <option value="">none</option>
                {causes.map((c) => <option key={c.id} value={c.id}>{c.tag}</option>)}
              </select>
            </label>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-white/65">Cause %</span>
              <input data-testid="curator-split-cause" type="number" min="0" max="100" value={form.split_pct_to_cause} onChange={set('split_pct_to_cause', (v) => parseInt(v || '0'))} className="mt-1 w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-white/65">Celebrity %</span>
              <input data-testid="curator-split-celeb" type="number" min="0" max="100" value={form.split_pct_to_celebrity} onChange={set('split_pct_to_celebrity', (v) => parseInt(v || '0'))} className="mt-1 w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none focus:border-bx-cyan text-sm" />
            </label>
            <div>
              <span className="text-xs uppercase tracking-widest text-white/65">Platform %</span>
              <div className={`mt-1 px-3 py-3 rounded-xl bg-white/5 border border-white/10 text-sm font-bold ${platformPct < 0 ? 'text-bx-magenta' : 'text-bx-volt'}`}>
                {platformPct}%
              </div>
            </div>
          </div>

          <label className="block">
            <span className="text-xs uppercase tracking-widest text-white/65">Face reference image</span>
            <div className="mt-1 flex items-center gap-3">
              <label className="cursor-pointer flex items-center gap-2 px-3 py-3 rounded-xl bg-white/5 border border-white/10 hover:border-bx-cyan transition text-sm">
                <UploadSimple size={16} />
                <span>{filename || (form.face_ref_b64 ? 'Image attached' : 'Upload image')}</span>
                <input data-testid="curator-face-input" type="file" accept="image/*" onChange={onFile} className="hidden" />
              </label>
              {form.face_ref_b64 && (
                <img src={`data:image/png;base64,${form.face_ref_b64}`} alt="ref" className="h-14 w-14 rounded-full object-cover ring-1 ring-white/10" />
              )}
            </div>
            <p className="text-[11px] text-white/60 mt-1">Stored privately. Each mint stylizes a unique bubble from this reference.</p>
          </label>

          <button data-testid="curator-submit" onClick={submit} disabled={busy} className="w-full btn-primary py-3.5 rounded-full text-sm flex items-center justify-center gap-2 disabled:opacity-50 mt-3">
            {busy ? 'Generating preview…' : <>Publish drop <ArrowRight size={14} weight="bold" /></>}
          </button>
        </div>

        {/* Payouts panel */}
        <div className="lg:col-span-5 space-y-4">
          <div className="glass rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Crown size={18} weight="duotone" className="text-bx-gold" />
              <h2 className="font-display text-lg font-bold">Celebrity payouts</h2>
            </div>
            {payouts.by_celebrity.length === 0 ? (
              <div className="text-white/60 text-sm">No celebrity drops sold yet.</div>
            ) : (
              <div className="space-y-2">
                {payouts.by_celebrity.map((c) => (
                  <div key={c.celebrity_name} className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl p-3 text-sm" data-testid={`celeb-payout-${c.celebrity_name}`}>
                    <div>
                      <div className="font-bold">{c.celebrity_name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-white/60">{c.transactions} sales</div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-lg font-black text-bx-volt">${c.balance_due.toFixed(2)}</div>
                      <div className="text-[10px] uppercase tracking-widest text-white/60">owed</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="glass rounded-2xl p-5">
            <h2 className="font-display text-lg font-bold mb-3">Active celebrity drops</h2>
            {drops.length === 0 ? (
              <div className="text-white/60 text-sm">No celebrity drops yet. Create your first above.</div>
            ) : (
              <div className="space-y-3">
                {drops.map((d) => (
                  <Link key={d.id} to="/drops" data-testid={`curator-drop-${d.id}`} className="block bg-white/5 border border-white/10 rounded-xl p-3 hover:bg-white/10 transition">
                    <div className="flex items-center gap-3">
                      {bubbleImageSrc(d) ? (
                        <img src={bubbleImageSrc(d)} alt={d.name} className="h-14 w-14 rounded-full object-cover" />
                      ) : (
                        <div className="h-14 w-14 rounded-full bg-gradient-to-br from-bx-cyan to-bx-magenta" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-bold truncate">{d.name}</div>
                        <div className="text-[11px] text-white/65 truncate">{d.celebrity_name} · {d.sold}/{d.supply} sold</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-bx-volt">${d.price_usd}</div>
                        <div className="text-[10px] uppercase tracking-widest text-white/60">{d.rarity}</div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Curator;
